import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { todayIso } from '../../utils/formatters.js';

export function TerminateEmployeeModal({ employee, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [terminationDate, setTerminationDate] = useState(todayIso());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (employee) {
      setTerminationDate(todayIso());
      setError(null);
    }
  }, [employee]);

  const terminate = useMutation({
    mutationFn: ({ id, body }) => hrApi.terminateEmployee(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    terminate.mutate({ id: employee.id, body: { terminationDate } });
  }

  return (
    <Modal
      open={Boolean(employee)}
      onClose={onClose}
      title={t('hr.employees.terminate.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={submit} disabled={terminate.isPending}>
            {terminate.isPending ? `${t('common.save')}…` : t('hr.employees.action.terminate')}
          </Button>
        </>
      }
    >
      {employee && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            {t('hr.employees.terminate.confirm')}{' '}
            <strong>{employee.firstName} {employee.lastName}</strong> ({employee.employeeNumber}).
          </p>
          <label className="field">
            <span className="field-label">{t('hr.employees.terminate.date')}</span>
            <input
              className="input"
              type="date"
              value={terminationDate}
              onChange={(e) => setTerminationDate(e.target.value)}
              required
            />
          </label>
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
